import { apiClient, type StepUpRequestConfig } from "./client";

// Entra profile details, photos and the Admin's Edit person panel (O-R56, owner 2026-10-22). No type here carries the
// employee ID: the server only says whether one is set (hasEmployeeId). The new value is sent write-only (newEmployeeId).

export interface PersonDirectorySummary {
  displayNameSource: "override" | "entra" | "typed" | "upn" | string;
  typedDisplayName: string | null;
  jobTitle: string | null;
  department: string | null;
  managerDisplayName: string | null;
  hasPhoto: boolean;
  photoVersion: string | null;
  entraState: "unknown" | "found" | "not-found" | "disabled" | string;
  entraStateLabel: string;
  source: string;
  refreshedAtUtc: string | null;
  active: boolean;
  hasOverrides: boolean;
  valueKind: string;
}

export interface DirectoryOverride {
  field: string;
  fieldLabel: string;
  value: string | null;
  valueUpn: string | null;
  editedBy: string;
  editedAtUtc: string;
  reason: string;
  entraValue: string | null;
}

export interface PersonAccessState {
  active: boolean;
  pausedBy: string | null;
  pausedAtUtc: string | null;
  pauseReason: string | null;
  resumedBy: string | null;
  resumedAtUtc: string | null;
}

export interface DirectoryPerson {
  upn: string;
  displayName: string;
  displayNameSource: string;
  typedDisplayName: string | null;
  entraDisplayName: string | null;
  givenName: string | null;
  surname: string | null;
  jobTitle: string | null;
  entraJobTitle: string | null;
  department: string | null;
  entraDepartment: string | null;
  managerUpn: string | null;
  managerDisplayName: string | null;
  entraManagerDisplayName: string | null;
  hasEmployeeId: boolean;
  hasPhoto: boolean;
  photoSource: string | null;
  photoVersion: string | null;
  entraState: string;
  entraStateLabel: string;
  source: string;
  sourceLabel: string;
  refreshedAtUtc: string | null;
  overrides: DirectoryOverride[];
  access: PersonAccessState;
  adminNote: string | null;
  valueKinds: Record<string, string>;
}

export interface DirectoryPersonCard {
  upn: string;
  displayName: string;
  jobTitle: string | null;
  department: string | null;
  hasPhoto: boolean;
  photoVersion: string | null;
  valueKind: string;
}

export interface DirectoryStatus {
  graphEnabled: boolean;
  /** off | not-run | ok | waiting-for-it | failed */
  graphState: string;
  graphMessage: string;
  lastGraphRunAtUtc: string | null;
  lastBasicRefreshAtUtc: string | null;
  basicRefreshDue: boolean;
  knownUpns: string[];
  profiles: number;
  notFound: number;
  disabled: number;
  paused: number;
  valueKinds: Record<string, string>;
}

export interface SelfDirectorySyncRequest {
  id: string | null;
  userPrincipalName: string | null;
  displayName: string | null;
  givenName: string | null;
  surname: string | null;
  jobTitle: string | null;
  department: string | null;
  employeeId: string | null;
  mail: string | null;
  managerDisplayName: string | null;
  managerUpn: string | null;
  photoState: string | null;
  photoBase64: string | null;
  photoContentType: string | null;
  requestedByPerson?: boolean;
  useMicrosoftPhoto?: boolean;
}

export interface SelfDirectorySyncResult {
  applied: boolean;
  message: string;
  changedFields: string[];
  photoStored: boolean;
}

export interface AdminBasicPerson {
  upn: string;
  found: boolean;
  id: string | null;
  displayName: string | null;
  givenName: string | null;
  surname: string | null;
  mail: string | null;
  photoState: string | null;
  photoBase64: string | null;
  photoContentType: string | null;
}

export interface AdminBasicRefreshResult {
  accepted: number;
  updated: number;
  unchanged: number;
  photosStored: number;
  notFound: number;
  refused: string[];
  message: string;
}

export interface GraphSyncResult {
  state: string;
  message: string;
  read: number;
  updated: number;
  notFound: number;
  disabled: number;
  photosStored: number;
}

export interface EditDirectoryPersonRequest {
  displayName?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  managerUpn?: string | null;
  managerDisplayName?: string | null;
  /** Write-only: the stored value is never shown or returned. */
  newEmployeeId?: string | null;
  adminNote?: string | null;
  useEntraFor?: string[] | null;
  reason: string;
}

export const DIRECTORY_QK = {
  all: ["directory"] as const,
  status: ["directory", "status"] as const,
  person: (upn: string) => ["directory", "person", upn.toLowerCase()] as const,
  cards: ["directory", "cards"] as const,
  photo: (upn: string, version: string | null) => ["directory", "photo", upn.toLowerCase(), version ?? "none"] as const
};

const quiet: StepUpRequestConfig = { skipStepUpPrompt: true };
const enc = (upn: string) => encodeURIComponent(upn);

export const directoryApi = {
  syncMine: async (body: SelfDirectorySyncRequest): Promise<SelfDirectorySyncResult> =>
    (await apiClient.put<SelfDirectorySyncResult>("/profile/me/directory", body, quiet)).data,
  status: async (): Promise<DirectoryStatus> => (await apiClient.get<DirectoryStatus>("/admin/directory")).data,
  basicRefresh: async (people: AdminBasicPerson[]): Promise<AdminBasicRefreshResult> =>
    (await apiClient.put<AdminBasicRefreshResult>("/admin/directory/basic", { people })).data,
  refreshNow: async (): Promise<GraphSyncResult> => (await apiClient.post<GraphSyncResult>("/admin/directory/refresh")).data,
  cards: async (): Promise<DirectoryPersonCard[]> => (await apiClient.get<DirectoryPersonCard[]>("/directory/people")).data,
  person: async (upn: string): Promise<DirectoryPerson> => (await apiClient.get<DirectoryPerson>(`/admin/directory/people/${enc(upn)}`)).data,
  edit: async (upn: string, body: EditDirectoryPersonRequest): Promise<DirectoryPerson> =>
    (await apiClient.put<DirectoryPerson>(`/admin/directory/people/${enc(upn)}`, body)).data,
  pause: async (upn: string, reason: string): Promise<DirectoryPerson> =>
    (await apiClient.post<DirectoryPerson>(`/admin/directory/people/${enc(upn)}/pause`, { reason, confirmSignature: true })).data,
  resume: async (upn: string, reason: string): Promise<DirectoryPerson> =>
    (await apiClient.post<DirectoryPerson>(`/admin/directory/people/${enc(upn)}/resume`, { reason, confirmSignature: true })).data,
  /** A known person's photo, or null (204, none, or it cannot be read). */
  photo: async (upn: string): Promise<Blob | null> => {
    try {
      const config: StepUpRequestConfig = { responseType: "blob", skipStepUpPrompt: true };
      const res = await apiClient.get<Blob>(`/people/${enc(upn)}/photo`, config);
      return res.status === 200 && res.data instanceof Blob && res.data.size > 0 ? res.data : null;
    } catch {
      return null;
    }
  }
};
